import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { ImageUpload } from '../components/ImageUpload';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('case-studies');
  const [caseStudies, setCaseStudies] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestimonialDialogOpen, setIsTestimonialDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    client: '',
    description: '',
    image: '',
    services: '',
    results: ''
  });
  const [testimonialFormData, setTestimonialFormData] = useState({
    name: '',
    position: '',
    company: '',
    quote: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    checkAuth();
    fetchCaseStudies();
    fetchTestimonials();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch case studies",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch testimonials",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
  };

  const openDialog = (caseStudy = null) => {
    if (caseStudy) {
      setEditingId(caseStudy.id);
      setFormData({
        title: caseStudy.title,
        client: caseStudy.client,
        description: caseStudy.description,
        image: caseStudy.image,
        services: caseStudy.services.join(', '),
        results: caseStudy.results
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        client: '',
        description: '',
        image: '',
        services: '',
        results: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const payload = {
      ...formData,
      services: formData.services.split(',').map(s => s.trim())
    };

    try {
      if (editingId) {
        await axios.put(`${API}/case-studies/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Case study updated successfully",
        });
      } else {
        await axios.post(`${API}/case-studies`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Case study created successfully",
        });
      }
      setIsDialogOpen(false);
      fetchCaseStudies();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save case study",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/case-studies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Case study deleted successfully",
      });
      fetchCaseStudies();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete case study",
        variant: "destructive"
      });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast({
        title: "Success",
        description: "Password changed successfully",
      });
      setIsPasswordDialogOpen(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  const openTestimonialDialog = (testimonial = null) => {
    if (testimonial) {
      setEditingId(testimonial.id);
      setTestimonialFormData({
        name: testimonial.name,
        position: testimonial.position,
        company: testimonial.company,
        quote: testimonial.quote
      });
    } else {
      setEditingId(null);
      setTestimonialFormData({
        name: '',
        position: '',
        company: '',
        quote: ''
      });
    }
    setIsTestimonialDialogOpen(true);
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingId) {
        await axios.put(`${API}/testimonials/${editingId}`, testimonialFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Testimonial updated successfully",
        });
      } else {
        await axios.post(`${API}/testimonials`, testimonialFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Success",
          description: "Testimonial created successfully",
        });
      }
      setIsTestimonialDialogOpen(false);
      fetchTestimonials();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save testimonial",
        variant: "destructive"
      });
    }
  };

  const handleTestimonialDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Testimonial deleted successfully",
      });
      fetchTestimonials();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete testimonial",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_5ce93daa-3d59-42cf-bffd-24916c9ccd9c/artifacts/2ow58km4_%5BINSAT%5D%20Logo%20%28medium%29.png"
                alt="Insat Techno Services"
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setIsPasswordDialogOpen(true)}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Key className="mr-2" size={18} />
                Change Password
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                <LogOut className="mr-2" size={18} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex items-center space-x-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('case-studies')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'case-studies'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Case Studies
          </button>
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'testimonials'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Testimonials
          </button>
        </div>

        {/* Case Studies Tab */}
        {activeTab === 'case-studies' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Manage Case Studies</h2>
              <Button
                onClick={() => openDialog()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Plus className="mr-2" size={20} />
                Add Case Study
              </Button>
            </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading case studies...</p>
          </div>
        ) : caseStudies.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 text-lg">No case studies yet. Create your first one!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((study) => (
              <Card key={study.id} className="border-0 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{study.title}</h3>
                  <p className="text-sm text-red-600 mb-3">{study.client}</p>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{study.description}</p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => openDialog(study)}
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Edit className="mr-2" size={16} />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(study.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="mr-2" size={16} />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Manage Testimonials</h2>
              <Button
                onClick={() => openTestimonialDialog()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Plus className="mr-2" size={20} />
                Add Testimonial
              </Button>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading testimonials...</p>
              </div>
            ) : testimonials.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <p className="text-gray-600 text-lg">No testimonials yet. Create your first one!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((testimonial) => (
                  <Card key={testimonial.id} className="border-0 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="mb-4">
                        <svg className="w-8 h-8 text-red-600 mb-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                        <p className="text-gray-700 italic mb-4 line-clamp-4">"{testimonial.quote}"</p>
                      </div>
                      <div className="mb-4">
                        <div className="font-bold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.position}</div>
                        <div className="text-sm text-red-600">{testimonial.company}</div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => openTestimonialDialog(testimonial)}
                          size="sm"
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Edit className="mr-2" size={16} />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleTestimonialDelete(testimonial.id)}
                          size="sm"
                          variant="outline"
                          className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="mr-2" size={16} />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingId ? 'Edit Case Study' : 'Add New Case Study'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <Input
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Project Title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client *
              </label>
              <Input
                required
                value={formData.client}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                placeholder="Client Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Project description..."
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image *
              </label>
              <ImageUpload
                currentImage={formData.image}
                onImageUploaded={(url) => setFormData({ ...formData, image: url })}
              />
              <p className="text-xs text-gray-500 mt-2">
                Or paste image URL:
              </p>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Services (comma separated) *
              </label>
              <Input
                required
                value={formData.services}
                onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                placeholder="CCTV Surveillance, Fire Alarm Systems"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Results *
              </label>
              <Textarea
                required
                value={formData.results}
                onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                placeholder="Measurable results achieved..."
                rows={3}
              />
            </div>

            <div className="flex space-x-4">
              <Button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {editingId ? 'Update' : 'Create'} Case Study
              </Button>
              <Button
                type="button"
                onClick={() => setIsDialogOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password *
              </label>
              <Input
                type="password"
                required
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password *
              </label>
              <Input
                type="password"
                required
                minLength={6}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password *
              </label>
              <Input
                type="password"
                required
                minLength={6}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>

            <div className="flex space-x-4">
              <Button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Change Password
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setIsPasswordDialogOpen(false);
                  setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={isTestimonialDialogOpen} onOpenChange={setIsTestimonialDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingId ? 'Edit Testimonial' : 'Add New Testimonial'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTestimonialSubmit} className="space-y-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <Input
                required
                value={testimonialFormData.name}
                onChange={(e) => setTestimonialFormData({ ...testimonialFormData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position *
              </label>
              <Input
                required
                value={testimonialFormData.position}
                onChange={(e) => setTestimonialFormData({ ...testimonialFormData, position: e.target.value })}
                placeholder="CEO"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <Input
                required
                value={testimonialFormData.company}
                onChange={(e) => setTestimonialFormData({ ...testimonialFormData, company: e.target.value })}
                placeholder="Company Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quote/Testimonial *
              </label>
              <Textarea
                required
                value={testimonialFormData.quote}
                onChange={(e) => setTestimonialFormData({ ...testimonialFormData, quote: e.target.value })}
                placeholder="What did they say about your services..."
                rows={5}
              />
            </div>

            <div className="flex space-x-4">
              <Button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {editingId ? 'Update' : 'Create'} Testimonial
              </Button>
              <Button
                type="button"
                onClick={() => setIsTestimonialDialogOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};